import React from 'react';

/* ─── Section Label ─── */
export const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <p style={{
    fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.6)', margin: '0 0 10px', fontWeight: 600,
  }}>{text}</p>
);

/* ─── Shimmer block helper ─── */
export const Shimmer: React.FC<{ width?: string; height?: string; borderRadius?: string; style?: React.CSSProperties }> = ({ width = '100%', height = '14px', borderRadius = '4px', style }) => (
  <div style={{ width, height, borderRadius, background: 'rgba(255,255,255,0.06)', animation: 'pulse 1.5s ease-in-out infinite', ...style }} />
);

/* ─── CPS Ring Skeleton ─── */
export const CpsRingSkeleton: React.FC = () => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '16px',
    padding: '16px 18px', borderRadius: '16px',
    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.1)',
    marginBottom: '16px',
  }}>
    <Shimmer width="68px" height="68px" borderRadius="50%" />
    <div style={{ flex: 1 }}>
      <Shimmer width="140px" height="16px" style={{ marginBottom: '6px' }} />
      <Shimmer width="90px" height="11px" style={{ marginBottom: '10px' }} />
      <div style={{ display: 'flex', gap: '6px' }}>
        <Shimmer width="60px" height="18px" borderRadius="10px" />
        <Shimmer width="70px" height="18px" borderRadius="10px" />
        <Shimmer width="55px" height="18px" borderRadius="10px" />
      </div>
    </div>
  </div>
);

/* ─── Attribute Spotlight Skeleton ─── */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const AttrSpotlightSkeleton: React.FC = () => (
  <div style={{
    padding: '16px 18px', borderRadius: '14px', marginBottom: '24px',
    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
      <Shimmer width="120px" height="14px" />
      <Shimmer width="30px" height="14px" />
    </div>
    <Shimmer width="100%" height="4px" style={{ marginBottom: '10px' }} />
    <Shimmer width="80%" height="12px" />
  </div>
);

/* ─── Tarot Card Skeleton ─── */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const TarotSkeleton: React.FC = () => (
  <div className="aurora-glow-card" style={{ borderRadius: '16px', padding: '3px', marginBottom: '24px' }}>
    <div style={{ background: '#0e0e14', borderRadius: '14px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
      <Shimmer width="100px" height="154px" borderRadius="8px" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <Shimmer width="80px" height="10px" style={{ marginBottom: '10px' }} />
        <Shimmer width="160px" height="20px" style={{ marginBottom: '12px' }} />
        <Shimmer width="100%" height="14px" style={{ marginBottom: '6px' }} />
        <Shimmer width="80%" height="14px" />
      </div>
    </div>
  </div>
);

/* ─── Loading Skeleton ─── */
export const PulseSkeleton: React.FC = () => (
  <div style={{ marginBottom: '24px' }}>
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '16px', padding: '24px 20px', minHeight: '160px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div>
          <div style={{ width: '80px', height: '10px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', marginBottom: '6px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ width: '120px', height: '14px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
      </div>
      <div style={{ width: '90%', height: '16px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ width: '70%', height: '16px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', animation: 'pulse 1.5s ease-in-out infinite' }} />
    </div>
  </div>
);

/* ─── Explore Card ─── */
export const ExploreCard: React.FC<{ icon?: string; image?: string; title: string; subtitle: string; accent: string; onClick: () => void }> = ({ icon, image, title, subtitle, accent, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '14px 16px', borderRadius: '14px', cursor: 'pointer',
      background: `linear-gradient(135deg, ${accent}08, rgba(10,10,15,0.95))`,
      border: `1px solid ${accent}20`,
      marginBottom: '10px', transition: 'all 0.2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = `${accent}40`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = `${accent}20`; e.currentTarget.style.transform = 'none'; }}
  >
    {image ? (
      <img src={image} alt={title} style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `1px solid ${accent}30` }} />
    ) : (
      <span style={{ fontSize: '22px', flexShrink: 0 }}>{icon}</span>
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', color: '#f4e4c1', fontWeight: 600, fontFamily: "Cinzel, serif" }}>{title}</div>
      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{subtitle}</div>
    </div>
    <span style={{ fontSize: '14px', color: `${accent}60`, flexShrink: 0 }}>→</span>
  </div>
);
